/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
  InputNumber,
} from 'antd';
import moment from 'moment';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { checkToken } from 'api/utils';

// APIs
import { getEstadosGlobales } from 'api/catalogos/index';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import {
  getPercepcionesDescuento,
  getPercepcionDescuento,
  postPercepcionDescuento,
  patchPercepcionDescuento,
  deletePercepcionDescuento,
} from 'api/catalogos/percepcionesDescuento';

import { ESTATUS_ACTIVO } from 'utils/constants';

const IncidenciaPercepcion = ({ incidenciaId, disabled: parentDisabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);

  // Estados independientes (catálogos)
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposPercepcion, setTiposPercepcion] = useState([]);
  const [selectedData, setSelectedData] = useState({});

  // Simulación de objeto de permiso interno para este sub-componente
  const permission = {
    permissionModel: 'incidenciapercepciondescuento',
    catalogsModels: [],
    tabsModels: [],
  };

  const fetchData = async () => {
    if (!incidenciaId) return;
    setLoading(true);
    const _data = await getPercepcionesDescuento(incidenciaId);
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _percepciones,
        _gridData,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPercepciones(), // Carga de catálogo de percepciones
        incidenciaId ? getPercepcionesDescuento(incidenciaId) : [],
      ]);

      if (mounted) {
        setEstados(_estados);
        setTiposPercepcion(formatReceived(_percepciones));
        setData(formatReceived(_gridData));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [incidenciaId]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = () => {
    if (selectedData) {
      form.setFieldsValue({
        ...selectedData,
        vigencia_final: selectedData.vigencia_final ? moment(selectedData.vigencia_final) : null,
      });
      setVisible(true);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const [key] = selectedRowKeys;

      const payload = {
        ...values,
        incidencia: incidenciaId,
        vigencia_final: values.vigencia_final ? values.vigencia_final.format('YYYY-MM-DD') : null,
      };

      const saved = await (key
        ? patchPercepcionDescuento(key, payload)
        : postPercepcionDescuento(payload));

      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const deleted = await deletePercepcionDescuento(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const columns = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      render: (val) => {
        const p = tiposPercepcion.find((item) => item.id === val);
        return p ? `${p.clave} - ${p.descripcion}` : val;
      },
    },
    {
      titleText: 'Orden de Aplicación',
      dataIndex: 'orden_de_aplicacion',
      key: 'orden_de_aplicacion',
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  return (
    <Col span={24} style={{ marginTop: '20px' }}>
      <Spin tip="Cargando catálogos..." spinning={loading}>
        <Table
          title="AGREGAR PERCEPCIÓN PARA DESCUENTO"
          cols={columns}
          data={data}
          rowSelection={{ selectedRowKeys, type: 'radio' }}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd: () => {
              form.resetFields();
              setSelectedRowKeys([]);
              setVisible(true);
            },
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => setVisibleAlert(true),
          }}
          permission={{
            hasAllViewPermission: true,
            add: true,
            change: true,
            delete: true,
          }}
          hasAllViewPermission
        />

        <Modal
          open={open}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Percepción para Descuento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}

            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            name="percepcion_descuento_form"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  name="percepcion"
                  label="Percepción"
                  rules={[{ required: true, message: 'Requerido' }]}
                >
                  <Select
                    dataSource={tiposPercepcion}
                    keyProp="id"
                    render={(item) => `${item.clave} - ${item.descripcion}`}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="orden_de_aplicacion"
                  label="Orden de Aplicación"
                  rules={[{ required: true, message: 'Requerido' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="Número" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="esta percepción de descuento"
          loading={loading}
        />
      </Spin>
    </Col>
  );
};

export default IncidenciaPercepcion;
